export const scrollVisualizerCardIntoView = async (element: HTMLElement | null) => {
  if (!element) {
    return;
  }

  const slickSlide = element.closest<HTMLElement>('.slick-slide');
  const slickSlider = element.closest<HTMLElement>('.slick-slider');

  if (slickSlide && slickSlider) {
    await moveSlickToSlide(slickSlide, slickSlider);
    scrollElementVerticallyIntoView(element);
    return;
  }

  element.scrollIntoView({
    block: 'center',
    inline: 'center',
    behavior: 'auto',
  });
};

const moveSlickToSlide = async (slide: HTMLElement, slider: HTMLElement) => {
  const targetIndex = Number(slide.getAttribute('data-index'));

  if (Number.isNaN(targetIndex)) {
    return;
  }

  if (isSlideActive(slider, targetIndex)) {
    return;
  }

  const targetDot = slider.querySelector<HTMLButtonElement>(
    `.slick-dots li:nth-child(${targetIndex + 1}) button`
  );

  if (targetDot) {
    targetDot.click();

    await waitForSlideToBecomeActive(slider, targetIndex);
    return;
  }

  await moveSlickWithArrows(slider, targetIndex);
};

const moveSlickWithArrows = async (slider: HTMLElement, targetIndex: number) => {
  const maximumAttempts = 50;
  let attempts = 0;

  while (!isSlideActive(slider, targetIndex) && attempts < maximumAttempts) {
    const activeIndexes = getActiveSlideIndexes(slider);
    const firstActiveIndex = activeIndexes[0] ?? 0;

    const arrowSelector = targetIndex < firstActiveIndex ? '.slick-prev' : '.slick-next';

    const arrow = slider.querySelector<HTMLElement>(arrowSelector);

    if (
      !arrow ||
      arrow.getAttribute('aria-disabled') === 'true' ||
      arrow.classList.contains('slick-disabled')
    ) {
      break;
    }

    arrow.click();

    await waitForActiveSlidesToChange(slider, activeIndexes);

    attempts += 1;
  }
};

const getActiveSlideIndexes = (slider: HTMLElement): number[] => {
  return Array.from(slider.querySelectorAll<HTMLElement>('.slick-slide.slick-active'))
    .map((activeSlide) => Number(activeSlide.getAttribute('data-index')))
    .filter((index) => !Number.isNaN(index));
};

const isSlideActive = (slider: HTMLElement, targetIndex: number): boolean => {
  return getActiveSlideIndexes(slider).includes(targetIndex);
};

const waitForSlideToBecomeActive = (
  slider: HTMLElement,
  targetIndex: number,
  timeout = 1000
): Promise<void> => {
  if (isSlideActive(slider, targetIndex)) {
    return Promise.resolve();
  }

  return new Promise((resolve) => {
    let hasResolved = false;

    const finish = () => {
      if (hasResolved) {
        return;
      }

      hasResolved = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve();
    };

    const observer = new MutationObserver(() => {
      if (isSlideActive(slider, targetIndex)) {
        finish();
      }
    });

    observer.observe(slider, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    const timeoutId = window.setTimeout(finish, timeout);
  });
};

const waitForActiveSlidesToChange = (
  slider: HTMLElement,
  previousIndexes: number[],
  timeout = 250
): Promise<void> => {
  return new Promise((resolve) => {
    let hasResolved = false;

    const previousValue = previousIndexes.join(',');

    const finish = () => {
      if (hasResolved) {
        return;
      }

      hasResolved = true;
      observer.disconnect();
      window.clearTimeout(timeoutId);
      resolve();
    };

    const observer = new MutationObserver(() => {
      const currentValue = getActiveSlideIndexes(slider).join(',');

      if (currentValue !== previousValue) {
        finish();
      }
    });

    observer.observe(slider, {
      subtree: true,
      attributes: true,
      attributeFilter: ['class', 'style'],
    });

    const timeoutId = window.setTimeout(finish, timeout);
  });
};

const scrollElementVerticallyIntoView = (element: HTMLElement) => {
  const rect = element.getBoundingClientRect();
  const top = window.scrollY + rect.top - (window.innerHeight - rect.height) / 2;

  window.scrollTo({
    top,
    behavior: 'auto',
  });
};
